import { Box, Typography } from '@mui/material';
import type { PlannerAllocation } from '@timelog/pm-shared';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { demandCellColor, heatCellColor, hoursCellColor } from './heat';
import {
  LEFT_PANE_WIDTH_PX,
  NAME_COL_PX,
  NUM_COL_PX,
  PLANNER_INDENT_PX,
  PLANNER_ROW_HEIGHT_PX,
  UTIL_BAR_WIDTH_PX,
  UTIL_COL_PX,
} from './plannerGeometry';
import type { PlannerMetric } from './PlannerToolbar';
import type { PlannerT } from './plannerWindow';
import type { PlannerRow as PlannerRowModel } from './resourcePlannerModel';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { MonoText } from '@/components/ui';
import { formatHours } from '@/lib';
import { stableColor } from '@/lib/roleColor';
import { zIndex } from '@/theme';

// Geometry-critical layout is plain flex Boxes: the theme gives Stack a
// default 8px gap (MuiStack defaultProps), which would silently widen the
// fixed columns.

export type PlannerRowProps = {
  row: PlannerRowModel;
  cellWidth: number;
  metric: PlannerMetric;
  onToggle: (row: PlannerRowModel) => void;
  onMoveHours: (allocation: PlannerAllocation, anchor: HTMLElement) => void;
};

const GROUP_KINDS = new Set(['deptGroup', 'unassignedGroup', 'roleGroup']);

const rowBackground = (row: PlannerRowModel): string => {
  if (row.kind === 'unassignedGroup') {
    return 'color-mix(in srgb, var(--color-coral-80) 8%, var(--bg-panel))';
  }
  return GROUP_KINDS.has(row.kind) ? 'var(--bg-sunken)' : 'var(--bg-panel)';
};

const groupIcon = (row: PlannerRowModel): { name: string; color: string } | null => {
  if (row.kind === 'deptGroup') return { name: 'apartment', color: 'var(--fg-2)' };
  if (row.kind === 'unassignedGroup') return { name: 'person_off', color: 'var(--color-coral-80)' };
  if (row.kind === 'unassignedRole') return { name: 'badge', color: 'var(--fg-2)' };
  if (row.kind === 'roleGroup') {
    return { name: 'badge', color: row.warn ? 'var(--color-coral-80)' : 'var(--fg-2)' };
  }
  if (row.kind === 'genericSlot' && !row.projectDotId) {
    return { name: 'help', color: 'var(--color-coral-80)' };
  }
  return null;
};

const cellColorOf = (
  row: PlannerRowModel,
  hours: number,
  capacity: number,
  ratio: number | null,
) =>
  row.cellStyle === 'heat'
    ? heatCellColor(ratio, hours)
    : row.cellStyle === 'demand'
      ? demandCellColor(hours)
      : hoursCellColor(hours, capacity);

const cellLabel = (
  row: PlannerRowModel,
  metric: PlannerMetric,
  hours: number,
  ratio: number | null,
  t: PlannerT,
): string => {
  if (row.cellStyle === 'heat' && metric === 'pct') {
    return ratio === null
      ? t('row.HoursCompact', { hours: formatHours(Math.round(hours)) })
      : `${String(Math.round(ratio * 100))}%`;
  }
  return formatHours(Math.round(hours));
};

const numberCell = (value: number | null, warn?: boolean) => (
  <Box
    sx={{
      width: NUM_COL_PX,
      flex: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      height: '100%',
      pr: 1.5,
      borderLeft: 'var(--border-width-thin) solid var(--border-subtle)',
    }}
  >
    <MonoText size="label" color={warn ? 'var(--color-coral-100)' : 'var(--fg-2)'}>
      {value === null ? '—' : formatHours(Math.round(value))}
    </MonoText>
  </Box>
);

/** One planner row: the frozen 500px pane plus its period cells. */
export const PlannerRowView = ({
  row,
  cellWidth,
  metric,
  onToggle,
  onMoveHours,
}: PlannerRowProps) => {
  const { t } = useTranslation(['pmResources', 'pmCommon']);
  const background = rowBackground(row);
  const icon = groupIcon(row);
  const overRatio = row.utilRatio !== null && row.utilRatio > 1;

  return (
    <Box
      data-automation-id="planner-row"
      data-row-key={row.key}
      data-row-kind={row.kind}
      onClick={row.expandable ? () => onToggle(row) : undefined}
      sx={{
        display: 'flex',
        height: PLANNER_ROW_HEIGHT_PX,
        borderBottom: 'var(--border-width-thin) solid var(--border-subtle)',
        cursor: row.expandable ? 'pointer' : 'default',
        bgcolor: background,
        '&:hover': { bgcolor: row.expandable ? 'var(--bg-hover)' : background },
        '& .rp-move': { opacity: 0, transition: 'opacity var(--duration-instant)' },
        '&:hover .rp-move': { opacity: 1 },
      }}
    >
      {/* Frozen pane — sticky against the horizontal scroller. */}
      <Box
        sx={{
          display: 'flex',
          position: 'sticky',
          left: 0,
          width: LEFT_PANE_WIDTH_PX,
          flex: 'none',
          height: '100%',
          bgcolor: 'inherit',
          zIndex: zIndex.rowAffordance,
          borderRight: 'var(--border-width-thin) solid var(--border-divider)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: NAME_COL_PX,
            flex: 'none',
            alignItems: 'center',
            columnGap: '6px',
            minWidth: 0,
            pl: `${String(10 + row.depth * PLANNER_INDENT_PX)}px`,
            pr: 1,
          }}
        >
          {row.expandable ? (
            <MaterialSymbol
              name={row.expanded ? 'expand_more' : 'chevron_right'}
              size={18}
              color="var(--fg-3)"
            />
          ) : (
            <Box sx={{ width: 18, flex: 'none' }} />
          )}
          {icon && <MaterialSymbol name={icon.name} size={18} color={icon.color} />}
          {row.avatar && (
            <Box
              aria-hidden
              sx={{
                width: 22,
                height: 22,
                flex: 'none',
                borderRadius: 'var(--border-radius-pill)',
                bgcolor: `color-mix(in srgb, ${stableColor(row.avatar.id)} 24%, var(--bg-panel))`,
                color: 'var(--fg-1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="label">{row.avatar.initials}</Typography>
            </Box>
          )}
          {row.projectDotId && (
            <Box
              aria-hidden
              sx={{
                width: 8,
                height: 8,
                flex: 'none',
                borderRadius: 'var(--border-radius-small)',
                bgcolor: stableColor(row.projectDotId),
                // A dashed outline marks tentative (probability < 100) work.
                outline:
                  row.probability !== undefined && row.probability < 100
                    ? 'var(--border-width-thin) dashed var(--color-fog-80)'
                    : 'none',
                outlineOffset: '1px',
              }}
            />
          )}
          <Typography
            variant={GROUP_KINDS.has(row.kind) ? 'bodyBold' : 'bodyRegular'}
            sx={{
              color: row.col1Warn && row.kind === 'genericSlot' ? 'var(--fg-2)' : 'var(--fg-1)',
              // The name yields to nothing until 150px; the meta absorbs the rest.
              flex: 'none',
              maxWidth: 150,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {row.name}
          </Typography>
          {row.meta && (
            <Typography
              variant="label"
              sx={{
                color: 'var(--fg-3)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {row.meta}
            </Typography>
          )}
          {row.warn && (
            <Box component="span" title={row.warn} sx={{ display: 'inline-flex', flex: 'none' }}>
              <MaterialSymbol name="error" size={15} color="var(--color-coral-80)" />
            </Box>
          )}
          {row.kind === 'genericSlot' && row.allocation && (
            <Box
              component="button"
              type="button"
              className="rp-move"
              title={t('row.MoveHoursTitle')}
              data-automation-id="move-hours-button"
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                onMoveHours(row.allocation!, event.currentTarget);
              }}
              sx={{
                border: 0,
                p: 0,
                ml: 0.5,
                background: 'transparent',
                display: 'inline-flex',
                cursor: 'pointer',
                flex: 'none',
              }}
            >
              <MaterialSymbol name="move_up" size={17} color="var(--color-pink-80)" />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            width: UTIL_COL_PX,
            flex: 'none',
            alignItems: 'center',
            justifyContent: 'flex-end',
            columnGap: '8px',
            height: '100%',
            pr: 1.5,
            borderLeft: 'var(--border-width-thin) solid var(--border-subtle)',
          }}
        >
          {row.utilRatio !== null && (
            <>
              <Box
                sx={{
                  width: UTIL_BAR_WIDTH_PX,
                  height: 6,
                  flex: 'none',
                  borderRadius: 'var(--border-radius-small)',
                  bgcolor: 'var(--color-fog-20)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${String(Math.min(UTIL_BAR_WIDTH_PX, Math.round(row.utilRatio * UTIL_BAR_WIDTH_PX)))}px`,
                    height: '100%',
                    borderRadius: 'var(--border-radius-small)',
                    bgcolor: overRatio ? 'var(--color-coral-80)' : 'var(--color-blue-80)',
                  }}
                />
              </Box>
              <MonoText
                size="label"
                color={overRatio ? 'var(--color-coral-100)' : 'var(--fg-2)'}
                data-automation-id="planner-util-pct"
              >
                {`${String(Math.round(row.utilRatio * 100))}%`}
              </MonoText>
            </>
          )}
        </Box>

        {numberCell(row.col1, row.col1Warn)}
        {numberCell(row.col2)}
      </Box>

      {/* Track: one absolutely-positioned box per non-empty period. */}
      <Box
        sx={{
          position: 'relative',
          flex: 'none',
          height: '100%',
          width: `${String(row.cells.length * cellWidth)}px`,
        }}
      >
        {row.cells.map((cell, index) => {
          if (cell.hours <= 0) return null;
          const color = cellColorOf(row, cell.hours, cell.capacityHours, cell.ratio);
          return (
            <Box
              key={index}
              data-automation-id="planner-cell"
              data-over={color.over || undefined}
              sx={{
                position: 'absolute',
                top: 0,
                left: `${String(index * cellWidth)}px`,
                width: `${String(cellWidth)}px`,
                height: '100%',
                p: '5px 4px',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  borderRadius: 'var(--border-radius-small)',
                  background: color.background,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <MonoText size="label" color={color.foreground}>
                  {cellLabel(row, metric, cell.hours, cell.ratio, t)}
                </MonoText>
                {color.over && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 3,
                      right: 4,
                      width: 4,
                      height: 4,
                      borderRadius: 'var(--border-radius-pill)',
                      bgcolor: 'var(--color-black-00)',
                      opacity: 0.85,
                    }}
                  />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
